import type { CollectionModelFieldYesNo } from '@ikegps/core-shared';
import { addStyles, DefaultTheme } from '../../../components/theme';
import { T, Translate } from '../../../i18n';
import { m, type MithrilVNode } from '../../../mithril';
import type { ICollectionViewController } from '../collection.view.ctrl.interface';
import type { CollectionFieldView } from './field.view';

const CSS = {
	container: 'c-YesNoField',
	segment: 'c-YesNoField__Segment',
	isSelected: 'c-YesNoField__Segment--selected',
};

addStyles({
	[CSS.container]: {
		display: 'flex',
	},
	[CSS.segment]: {
		'font-size': '12px',
		'font-weight': 'bold',
		padding: `0 ${DefaultTheme.space(3)}`,
		'min-width': DefaultTheme.space(12),
		height: DefaultTheme.space(6),
		cursor: 'pointer',
		outline: 'none',
		background: 'none',
		color: DefaultTheme.font.color.supporting,
		border: `1px solid ${DefaultTheme.font.color.divider}`,
		// Collapse the shared edge so the pair reads as one control, and round only the outer corners.
		'&:first-child': {
			'border-radius': `${DefaultTheme.space(4)} 0 0 ${DefaultTheme.space(4)}`,
		},
		'&:last-child': {
			'border-radius': `0 ${DefaultTheme.space(4)} ${DefaultTheme.space(4)} 0`,
			'border-left': 'none',
		},
		'&:hover': {
			'background-color': DefaultTheme.background.lightGrey,
		},
		// These are real buttons with `outline: none`, so without this a keyboard user tabbing through
		// the form has no idea which segment they are on. :focus-visible keeps it off for mouse clicks.
		// Dark grey rather than accent — it has to read against both the plain and the accent-filled
		// segment; this is the same focus colour DrawButton uses.
		'&:focus-visible': {
			outline: `2px solid ${DefaultTheme.background.darkGrey}`,
			'outline-offset': '1px',
		},
	},
	[CSS.isSelected]: {
		// Accent fill is how the rest of the app marks a selected button (see DrawButton, and the
		// Manual/Collection Link field pills), so a selected segment reads as selected here too. With
		// neither segment filled, unanswered is self-evident — no cue text needed.
		'background-color': DefaultTheme.color.accent,
		color: DefaultTheme.font.color.white,
		'border-color': DefaultTheme.color.accent,
		'&:hover': {
			'background-color': DefaultTheme.color.accentDark,
		},
	},
});

/**
 * One half of the Yes/No pair. Clicking an unselected segment answers the field; clicking the
 * selected one takes it back to unanswered via the same handler the ✕ uses.
 */
function viewSegment(
	field: CollectionModelFieldYesNo,
	ctrl: ICollectionViewController,
	label: T,
	answer: boolean
): MithrilVNode {
	const selected = field.hasValue() && field.value === answer;

	return m(
		'button',
		{
			type: 'button',
			className: selected ? `${CSS.segment} ${CSS.isSelected}` : CSS.segment,
			'data-testid': `yesno-${answer ? 'yes' : 'no'}`,
			'data-selected': String(selected),
			onclick: () => (selected ? ctrl.clearField(field) : field.Mutator.manual(answer)),
		},
		Translate(label)
	);
}

export const YesNoFieldView: CollectionFieldView<CollectionModelFieldYesNo> = {
	viewInline(field: CollectionModelFieldYesNo): string | null {
		return field.hasValue() ? Translate(field.value ? T.YES : T.NO) : null;
	},

	viewDetail(field: CollectionModelFieldYesNo): MithrilVNode {
		return null;
	},

	viewEdit(field: CollectionModelFieldYesNo, ctrl: ICollectionViewController): MithrilVNode {
		return m('div', { className: CSS.container }, [
			viewSegment(field, ctrl, T.YES, true),
			viewSegment(field, ctrl, T.NO, false),
		]);
	},
};
